import { useCallback, useEffect, useState } from 'react';

import { DeliveryZone, DeliveryZoneCharge, DeliveryZoneChargeField } from '../types/delivery-zone.type';
import { UpdateDeliveryZoneRequestDTO } from '../types/dtos/update-delivery-zone.dto';
import {
  DEFAULT_CHARGE_THRESHOLDS,
  DELIVERY_ZONE_CHARGES_COUNT,
  areChargesValid,
  computeCostsTTC,
  toChargeInputs,
} from '../utils/delivery-zone-charges.util';
import { isDeliveryZoneNameValid } from '../utils/delivery-zone-form-rules.util';

type EditFormState = {
  updatedDeliveryZone: DeliveryZone;
  editedFields: UpdateDeliveryZoneRequestDTO;
  editedChargeRows: boolean[];
};

type UseDeliveryZoneEditFormResult = {
  updatedDeliveryZone: DeliveryZone;
  editedFields: UpdateDeliveryZoneRequestDTO;
  isEditing: boolean;
  isNameInvalid: boolean;
  areChargesInvalid: boolean;
  needsCharges: boolean;
  hasInvalidEditedField: boolean;
  handleNameChange: (name: string) => void;
  handleChargeChange: (index: number, field: DeliveryZoneChargeField, value: number) => void;
  handleSave: () => Promise<void>;
  resetForm: () => void;
};

const DEFAULT_CHARGES: DeliveryZoneCharge[] = DEFAULT_CHARGE_THRESHOLDS.map((threshold) => ({
  threshold,
  costsHT: 0,
  costsTTC: 0,
}));

const toDisplayedCharges = (charges: DeliveryZoneCharge[]): DeliveryZoneCharge[] =>
  charges.length < DELIVERY_ZONE_CHARGES_COUNT
    ? DEFAULT_CHARGES.map((defaultCharge, index) => charges[index] ?? defaultCharge)
    : charges;

const toInitialForm = (deliveryZone: DeliveryZone): EditFormState => {
  const displayedCharges = toDisplayedCharges(deliveryZone.charges);

  return {
    updatedDeliveryZone: { ...deliveryZone, charges: displayedCharges },
    editedFields: {},
    editedChargeRows: displayedCharges.map(() => false),
  };
};

export function useDeliveryZoneEditForm(
  deliveryZone: DeliveryZone,
  updateDeliveryZone: (data: UpdateDeliveryZoneRequestDTO) => Promise<DeliveryZone | null>
): UseDeliveryZoneEditFormResult {
  const [form, setForm] = useState<EditFormState>(() => toInitialForm(deliveryZone));

  const resetForm = useCallback(() => setForm(toInitialForm(deliveryZone)), [deliveryZone]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  const handleNameChange = useCallback(
    (name: string) =>
      setForm((previous) => ({
        ...previous,
        updatedDeliveryZone: { ...previous.updatedDeliveryZone, name },
        editedFields: { ...previous.editedFields, name },
      })),
    []
  );

  const handleChargeChange = useCallback(
    (index: number, field: DeliveryZoneChargeField, value: number) =>
      setForm((previous) => {
        const charges = previous.updatedDeliveryZone.charges.map((charge, chargeIndex) => {
          if (chargeIndex !== index) return charge;

          const editedCharge = { ...charge, [field]: value };
          return { ...editedCharge, costsTTC: computeCostsTTC(editedCharge.costsHT) };
        });

        return {
          updatedDeliveryZone: { ...previous.updatedDeliveryZone, charges },
          editedFields: { ...previous.editedFields, charges: toChargeInputs(charges) },
          editedChargeRows: previous.editedChargeRows.map((isEdited, rowIndex) => isEdited || rowIndex === index),
        };
      }),
    []
  );

  const { updatedDeliveryZone, editedFields, editedChargeRows } = form;
  const isEditing = Object.keys(editedFields).length > 0;
  const isNameInvalid = editedFields.name !== undefined && !isDeliveryZoneNameValid(editedFields.name);
  const areChargesInvalid = editedFields.charges !== undefined && !areChargesValid(editedFields.charges);
  const needsCharges = editedChargeRows.some(
    (isEdited, rowIndex) => rowIndex >= deliveryZone.charges.length && !isEdited
  );
  const hasInvalidEditedField = isNameInvalid || areChargesInvalid || needsCharges;

  const handleSave = async (): Promise<void> => {
    if (!isEditing || hasInvalidEditedField) return;

    await updateDeliveryZone(editedFields);
  };

  return {
    updatedDeliveryZone,
    editedFields,
    isEditing,
    isNameInvalid,
    areChargesInvalid,
    needsCharges,
    hasInvalidEditedField,
    handleNameChange,
    handleChargeChange,
    handleSave,
    resetForm,
  };
}
